import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import OrganizationSelect from "../components/OrganizationSelect/OrganizationSelect";
import api from "../../../api/axios";

export default function OrganizationUsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async (orgId = '') => {
    try {
      setLoading(true);

      const url = orgId
        ? `/user/list?role=user&org_id=${orgId}`
        : "/user/list?role=user";

      const response = await api.get(url);

      if (response?.data?.success) {
        setUsers(response.data.data.list || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.log(err?.response?.data?.message || err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    return users.filter((item) =>
      (item.user_name || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [users, search]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Organization users</h2>
        <p className="mt-1 text-sm text-slate-500">
          Support and monitor users across all organizations.
        </p>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b p-4">
          <label className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users…"
              className="w-full rounded-lg border px-9 py-2 text-sm"
            />
          </label>
          <OrganizationSelect
            value={organization}
            onChange={(orgId) => {
              setOrganization(orgId);
              loadOrganizations(orgId);
            }}
            className="w-56"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "User",
                  "Organization",
                  "Role",
                  "Mobile",
                  "Status"
                ].map(
                  (x) => (
                    <th key={x} className="px-5 py-3">
                      {x}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">

              {loading && (
                <tr>
                  <td colSpan={6} className="py-10 text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    No users found
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-5 py-4">
                      <div>
                        <div className="font-semibold text-slate-800">
                          {user.user_name}
                        </div>

                        <div className="text-xs text-slate-500">
                          {user.email}
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      {user.org_name || "-"}
                    </td>

                    <td className="px-5 py-4">
                      <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                        {user.role}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      {user.mobile || "-"}
                    </td>

                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${user.status === "ACTIVE"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
